import { useNavigate, useParams } from 'react-router-dom';
import usePermission from '../../../hooks/usePermission';
import React, { startTransition, useEffect } from 'react';
import {
  HeaderSection,
  AppTable,
  useFetchList,
  PAGE_SIZE_OPTIONS,
  IAppTableColumn,
  BaseResponseProps,
} from '@qlibs/react-components';
import RowFilter from '@qlibs/react-components/dist/Table/RowFilter';
import {
  Button,
  Dropdown,
  Grid,
  MenuProps,
  Modal,
  Switch,
  Tag,
  message,
} from 'antd';
import { httpRequest } from '../../../helpers/api';
import { EDateFormat } from '../../../const/config';
import { AssetProperties } from '../../asset/types/asset.type';
import LinkTable from '../../../components/LinkTable';
import {
  OperationModeDetailProperties,
  OperationModeProperties,
  Ramus2OperationModeSpeedConfig,
  isClutchOperationModeDevice,
} from '../types/operationMode.type';
import useMasterData from '../../../hooks/useMasterData';
import useGroup from '../../../zustand/useGroup';
import { useAuthUser } from 'react-auth-kit';
import { canUpdateRamus2OperationModeSpeedConfig } from '../../asset/assetAliasSourceAccess';
import {
  EFormatCurrentDate,
  formatCurrentDate,
} from '../../../helpers/formatCurrentDate';
import styled from 'styled-components';
import OperationModeDetailCreate from './Create';
import OperationModeDetailEdit from './Edit';
import {
  OperationModeMobileAddSquareButton,
  OperationModeMobileConstantModal,
  OperationModeMobileDetailActionRow,
  OperationModeMobileEmptyState,
  OperationModeMobileFilterActions,
  OperationModeMobileFilterApplyButton,
  OperationModeMobileFilterButton,
  OperationModeMobileFilterDrawer,
  OperationModeMobileFilterField,
  OperationModeMobileFilterFields,
  OperationModeMobileFilterForm,
  OperationModeMobileFilterRow,
  OperationModeMobileFilterSelect,
  OperationModeMobileMoreButton,
  OperationModeMobileSearch,
  OperationModeMobileStatus,
  OperationModeMobileTable,
  OperationModeMobileTableWrap,
} from '../mobile';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { RiEqualizer2Line, RiMore2Line } from '@remixicon/react';
const moment = require('moment');

// Shared by desktop table and mobile table.
function formatSpeed(record: OperationModeDetailProperties): string {
  if (record.speed == null || String(record.speed) === '') {
    return '-';
  }

  const speed = Number(record.speed);
  return record.speedOperator ? `${record.speedOperator}${speed}` : `${speed}`;
}

// Shared by desktop table and mobile table.
function formatRpmThreshold(record: OperationModeDetailProperties): string {
  const hasMin = record.rpmMin != null && String(record.rpmMin) !== '';
  const hasMax = record.rpmMax != null && String(record.rpmMax) !== '';

  if (hasMin && hasMax) {
    return `${Number(record.rpmMin)}-${Number(record.rpmMax)}`;
  }

  if (hasMin) {
    return `${Number(record.rpmMin)}<`;
  }

  return hasMax ? `${Number(record.rpmMax)}` : '-';
}

const FEATURE = 'OPERATION_MODE';

interface IProps {
  operationModeId: string;
  operationModeTitle?: string;
  ramus2AssetId?: string;
  isRamus2?: boolean;
}

const OperationModeDetail: React.FC<IProps> = ({
  operationModeId,
  operationModeTitle,
  ramus2AssetId,
  isRamus2 = false,
}) => {
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();
  const { getListCompanies, companies } = useMasterData();
  const auth = useAuthUser();
  const [parentDeviceId, setParentDeviceId] = React.useState('');
  const isClutchDevice = isClutchOperationModeDevice(parentDeviceId);
  const thresholdColumnTitle = isClutchDevice ? 'CLUTCH MAX' : 'RPM';
  const canUpdateRamus2SpeedConfig =
    !isRamus2 ||
    canUpdateRamus2OperationModeSpeedConfig(auth()?.role);
  const canUpdateOperationModeDetail =
    isUserHasPermission([FEATURE + '.UPDATE']) &&
    canUpdateRamus2SpeedConfig;

  const handleCreateDevice = () => {
    setCurrentData(undefined);
    setIsModalCreateOpen(true);
  };

  const [isModalCreateOpen, setIsModalCreateOpen] = React.useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = React.useState(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = React.useState(false);
  const [isModalRestoreOpen, setIsModalRestoreOpen] = React.useState(false);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = React.useState(false);
  const [mobileFilterValues, setMobileFilterValues] = React.useState({
    search: '',
    status: 'all',
    filterData: 'default',
  });
  const [currentData, setCurrentData] =
    React.useState<OperationModeDetailProperties>();
  const [dataAsset, setDataAsset] = React.useState<AssetProperties[]>([]);
  const [forceDeleteId, setForceDeleteId] = React.useState<string>();
  const [ramus2SpeedConfig, setRamus2SpeedConfig] =
    React.useState<Ramus2OperationModeSpeedConfig>({
      compareOperationModeSpeed: false,
      speedMaxByOperationModeDetailId: {},
    });
  const [isRamus2SpeedConfigLoading, setIsRamus2SpeedConfigLoading] =
    React.useState(false);

  const {
    isLoading,
    data,
    pagination,
    query,
    setData,
    setQuery,
    changePage,
    changeLimit,
    handleSearch,
    pageQueries,
    filterDropdown,
    fetchList,
  } = useFetchList<OperationModeDetailProperties>({
    httpRequest: httpRequest as any,
    endpoint: 'operation-mode-detail',
    limit: +PAGE_SIZE_OPTIONS[1],
    initialQuery: {
      operationModeId,
    },
    pageQuery: {
      defaultValue: {
        page: 1,
      },
    },
  });

  useEffect(() => {
    if (!isRamus2 || !ramus2AssetId) {
      return;
    }

    async function fetchRamus2SpeedConfig() {
      try {
        setIsRamus2SpeedConfigLoading(true);
        const response = await httpRequest.get<
          BaseResponseProps<Ramus2OperationModeSpeedConfig>
        >(`/operation-mode-detail/ramus2/${ramus2AssetId}/speed-config`);
        const config = response.data.payload;

        if (config) {
          setRamus2SpeedConfig(config);
        }
      } catch (error) {
        message.error('Failed get Ramus 2.0 speed configuration');
      } finally {
        setIsRamus2SpeedConfigLoading(false);
      }
    }

    fetchRamus2SpeedConfig();
  }, [isRamus2, ramus2AssetId]);

  useEffect(() => {
    if (isRamus2 || !operationModeId) {
      return;
    }

    async function fetchParentOperationMode() {
      try {
        const response = await httpRequest.get<
          BaseResponseProps<OperationModeProperties>
        >('/operation-mode/' + operationModeId);
        const deviceId =
          response.data.payload?.device?.uniqueId ||
          response.data.payload?.deviceId ||
          '';
        setParentDeviceId(deviceId);
      } catch (error) {
        console.error(error);
      }
    }

    fetchParentOperationMode();
  }, [isRamus2, operationModeId]);

  async function updateRamus2SpeedComparison(enabled: boolean) {
    if (!ramus2AssetId) {
      return;
    }

    try {
      setIsRamus2SpeedConfigLoading(true);
      const response = await httpRequest.patch<
        BaseResponseProps<Ramus2OperationModeSpeedConfig>
      >(`/operation-mode-detail/ramus2/${ramus2AssetId}/speed-config`, {
        compareOperationModeSpeed: enabled,
      });
      const config = response.data.payload;

      setRamus2SpeedConfig(
        config || {
          ...ramus2SpeedConfig,
          compareOperationModeSpeed: enabled,
        },
      );
      message.success('Ramus 2.0 speed comparison updated');
    } catch (error) {
      message.error('Failed update Ramus 2.0 speed comparison');
    } finally {
      setIsRamus2SpeedConfigLoading(false);
    }
  }

  async function deleteData() {
    try {
      if (currentData) {
        const forceDelete =
          forceDeleteId && forceDeleteId === currentData.operationModeDetailId;
        await httpRequest.delete(
          '/operation-mode-detail/' + currentData.operationModeDetailId,
          {
            params: {
              forceDelete: forceDelete || undefined,
            },
          }
        );
        message.success(
          `Success ${forceDelete ? 'delete' : 'archive'} operation mode detail`
        );

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalDeleteOpen(false);
      setCurrentData(undefined);
    }
  }

  async function restoreData() {
    try {
      if (currentData) {
        await httpRequest.patch(
          '/operation-mode-detail/' +
            currentData.operationModeDetailId +
            '/restore'
        );
        message.success(`Success restore operation mode detail`);

        setQuery({
          ...query,
        });
      } else {
        message.error('Current data not found');
      }
    } catch (error: any) {
      message.error(error.data.message);
    } finally {
      setIsModalRestoreOpen(false);
      setCurrentData(undefined);
      setForceDeleteId(undefined);
    }
  }

  async function handleRestoreDevice(id: any) {
    setIsModalRestoreOpen(true);
    const found = data.find((item) => item.operationModeDetailId === id);
    if (found) {
      setCurrentData(found);
    }
  }

  async function handleDeleteDevice(id: any, forced?: boolean) {
    setIsModalDeleteOpen(true);
    const found = data.find((item) => item.operationModeDetailId === id);
    if (found) {
      setCurrentData(found);
    }

    if (forced) {
      setForceDeleteId(id);
    }
  }

  function handleCloseModal() {
    setIsModalDeleteOpen(false);
    setIsModalRestoreOpen(false);
    setIsModalCreateOpen(false);
    setIsModalEditOpen(false);
    setIsMobileFilterOpen(false);
    setCurrentData(undefined);
  }

  function handleOpenMobileFilter() {
    setMobileFilterValues({
      search: pageQueries.search || '',
      status: pageQueries.filterStatus || pageQueries.status || 'all',
      filterData:
        pageQueries.filterArchived || pageQueries.filterData || 'default',
    });
    setIsMobileFilterOpen(true);
  }

  function handleApplyMobileFilter() {
    setQuery({
      ...query,
      page: 1,
      search: mobileFilterValues.search,
      filterStatus:
        mobileFilterValues.status === 'all' ? '' : mobileFilterValues.status,
      filterArchived:
        mobileFilterValues.filterData === 'default'
          ? ''
          : mobileFilterValues.filterData,
    });
    setIsMobileFilterOpen(false);
  }

  function isArchived(id?: string) {
    if (id) {
      const found = data.find(
        (item) => item.operationModeDetailId === id && item.deletedAt
      );
      if (found) {
        return true;
      }
    }

    return false;
  }

  const filterCompanyOptions = React.useMemo(() => {
    const data = companies.map((item) => ({
      value: item.companyId,
      label: item.name,
    }));
    return [{ value: '', label: 'All' }, ...data];
  }, [companies]);

  const sortedData = React.useMemo(
    () => {
      const num = (value: string | number | null | undefined) =>
        value == null || String(value) === '' || !Number.isFinite(Number(value))
          ? Infinity
          : Number(value);

      return [...data].sort((left, right) => {
        const leftMax = num(left.rpmMax);
        const rightMax = num(right.rpmMax);
        const leftStart = isClutchDevice
          ? num(left.clutchMax)
          : num(left.rpmMin) !== Infinity
          ? num(left.rpmMin)
          : leftMax;
        const rightStart = isClutchDevice
          ? num(right.clutchMax)
          : num(right.rpmMin) !== Infinity
          ? num(right.rpmMin)
          : rightMax;

        if (leftStart !== rightStart) {
          return leftStart - rightStart;
        }

        if (!isClutchDevice && leftMax !== rightMax) {
          return leftMax - rightMax;
        }

        return (left.name || '').localeCompare(right.name || '', undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });
    },
    [data, isClutchDevice]
  );

  const columns: IAppTableColumn<OperationModeDetailProperties>[] = [
    {
      title: 'NAME',
      dataIndex: 'name',
      key: 'name',
      render: (value, records) => (
        <LinkTable
          onPress={() => {
            if (!canUpdateOperationModeDetail) {
              return;
            }
            setCurrentData(records);
            setIsModalEditOpen(true);
          }}
        >
          {records.deletedAt ? (
            <Tag color='#ff5500' className='mb-2'>
              Archived
            </Tag>
          ) : (
            false
          )}
          {records?.name}
        </LinkTable>
      ),
    },
    {
      title: thresholdColumnTitle,
      dataIndex: isClutchDevice ? 'clutchMax' : 'rpmMax',
      key: isClutchDevice ? 'clutchMax' : 'rpmMax',
      render: (_value, records) => (
        <div>
          {isClutchDevice
            ? records?.clutchMax ?? '-'
            : formatRpmThreshold(records)}
        </div>
      ),
    },
    {
      title: 'SPEED',
      dataIndex: 'speed',
      key: 'speed',
      render: (_value, records) => formatSpeed(records),
    },
    {
      title: 'CONST RATE',
      dataIndex: 'consumptionRate',
      key: 'consumptionRate',
      render: (value, records) => <div>{records?.consumptionRate} L/m</div>,
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      render: (value, _) => (
        <Tag color={value === 'active' ? 'success' : 'error'}>
          {value.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (value, _) =>
        formatCurrentDate(value, EFormatCurrentDate.DEFAULT_SHORT_MONTH),
    },
    {
      title: 'ACTION',
      key: 'action',
      type: 'actions',
      actions: [
        canUpdateOperationModeDetail
          ? {
              key: 'update',
              label: 'Update',
              onPress: (id) => {
                const found = data.find(
                  (item) => item.operationModeDetailId === id
                );
                if (found) {
                  setCurrentData(found);
                  setIsModalEditOpen(true);
                }
              },
            }
          : undefined,
        !isRamus2 && isUserHasPermission([FEATURE + '.DELETE'])
          ? {
              key: 'archive',
              label: 'Delete',
              color: '#ff4d4f',
              disabled: (id) => isArchived(id),
              disabledTooltip: 'This data is already archived',
              onPress: (id) => handleDeleteDevice(id),
            }
          : undefined,
        !isRamus2 && isUserHasPermission([FEATURE + '.RESTORE'])
          ? {
              key: 'restore',
              label: 'Restore',
              color: '#1890ff',
              disabled: (id) => !isArchived(id),
              disabledTooltip: 'This data not in archived',
              onPress: (id) => handleRestoreDevice(id),
            }
          : undefined,
        !isRamus2 && isUserHasPermission([FEATURE + '.FORCE_DELETE'])
          ? {
              key: 'delete',
              label: 'Force Delete',
              onPress: (id) => handleDeleteDevice(id, true),
            }
          : undefined,
      ],
    },
  ];

  const filterData: any = [
    {
      type: 'search',
      key: 'search',
      label: 'Search',
      placeholder: 'Search by name',
      onChange: (value: any) => {
        handleSearch(value);
      },
      colSpan: isUserHasPermission([FEATURE + '.DELETE']) ? 6 : 10,
    },
    {
      type: 'select',
      key: 'status',
      label: 'Status',
      options: [
        {
          value: 'all',
          label: 'All',
        },
        {
          value: 'active',
          label: 'Active',
        },
        {
          value: 'inactive',
          label: 'Inactive',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterStatus: value === 'all' ? '' : value,
        });
      },
      colSpan: 6,
      allowClear: false,
    },
    {
      type: 'select',
      key: 'filterData',
      label: 'Filter Data',
      options: [
        {
          value: 'default',
          label: 'Default',
        },
        {
          value: 'show',
          label: 'Include Archive Data',
        },
      ],
      onChange: (value: any) => {
        filterDropdown({
          filterArchived: value === 'default' ? '' : value,
        });
      },
      colSpan: 4,
      allowClear: false,
    },
  ].filter((item) =>
    !isUserHasPermission([FEATURE + '.DELETE'])
      ? item.key !== 'filterData'
      : item
  );

  const getMobileActionItems = (
    record: OperationModeDetailProperties,
  ): MenuProps['items'] => {
    const items: MenuProps['items'] = [];

    if (canUpdateOperationModeDetail) {
      items.push({ key: 'update', label: 'Update' });
    }

    if (!isRamus2 && isUserHasPermission([FEATURE + '.DELETE'])) {
      items.push({
        key: 'archive',
        label: 'Delete',
        danger: true,
        disabled: isArchived(record.operationModeDetailId),
      });
    }

    if (!isRamus2 && isUserHasPermission([FEATURE + '.RESTORE'])) {
      items.push({
        key: 'restore',
        label: 'Restore',
        disabled: !isArchived(record.operationModeDetailId),
      });
    }

    if (!isRamus2 && isUserHasPermission([FEATURE + '.FORCE_DELETE'])) {
      items.push({ key: 'delete', label: 'Force Delete', danger: true });
    }

    return items;
  };

  const handleMobileActionClick =
    (record: OperationModeDetailProperties) =>
    (info: MenuProps['onClick'] extends (arg: infer I) => any ? I : any) => {
      if (info?.key === 'update') {
        setCurrentData(record);
        setIsModalEditOpen(true);
      }

      if (info?.key === 'archive') {
        handleDeleteDevice(record.operationModeDetailId);
      }

      if (info?.key === 'restore') {
        handleRestoreDevice(record.operationModeDetailId);
      }

      if (info?.key === 'delete') {
        handleDeleteDevice(record.operationModeDetailId, true);
      }
    };
  const ConstantModal = isMobile ? OperationModeMobileConstantModal : Modal;

  return (
    <React.Fragment>
      {isRamus2 && ramus2AssetId && (
        <div
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div>
            <strong>Compare Speed</strong>
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>
              Use GPS speed with existing RPM constants.
            </div>
          </div>
          <Switch
            checked={ramus2SpeedConfig.compareOperationModeSpeed}
            disabled={
              isRamus2SpeedConfigLoading ||
              !canUpdateOperationModeDetail
            }
            onChange={updateRamus2SpeedComparison}
          />
        </div>
      )}
      {isMobile ? (
        <>
          <OperationModeMobileDetailActionRow>
            <OperationModeMobileFilterRow>
              <OperationModeMobileSearch
                allowClear
                placeholder="Search by constant name"
                defaultValue={pageQueries.search}
                suffix={<SearchOutlined />}
                onChange={(event) => handleSearch(event.target.value)}
              />
              <OperationModeMobileFilterButton
                type="button"
                aria-label="Open constant filters"
                onClick={handleOpenMobileFilter}
              >
                <RiEqualizer2Line />
              </OperationModeMobileFilterButton>
            </OperationModeMobileFilterRow>
            {!isRamus2 && isUserHasPermission([FEATURE + '.CREATE']) && (
              <OperationModeMobileAddSquareButton
                type="primary"
                aria-label="Add constant"
                onClick={handleCreateDevice}
              >
                <PlusOutlined />
              </OperationModeMobileAddSquareButton>
            )}
          </OperationModeMobileDetailActionRow>

          {isLoading ? (
            <OperationModeMobileEmptyState>
              Loading constants...
            </OperationModeMobileEmptyState>
          ) : !data.length ? (
            <OperationModeMobileEmptyState>
              No constant data found.
            </OperationModeMobileEmptyState>
          ) : (
            <OperationModeMobileTableWrap>
              <OperationModeMobileTable $minWidth={858}>
                <thead>
                  <tr>
                    <th style={{ width: 103 }}>Name</th>
                    <th style={{ width: 120 }}>
                      {isClutchDevice ? 'Clutch Max' : 'RPM'}
                    </th>
                    <th style={{ width: 120 }}>Speed</th>
                    <th style={{ width: 179 }}>Const Rate</th>
                    <th style={{ width: 105 }}>Status</th>
                    <th style={{ width: 159 }}>Updated At</th>
                    <th style={{ width: 112 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((item) => (
                    <tr key={item.operationModeDetailId}>
                      <td>{item.name}</td>
                      <td>
                        {isClutchDevice
                          ? item.clutchMax ?? '-'
                          : formatRpmThreshold(item)}
                      </td>
                      <td>
                        {formatSpeed(item)}
                      </td>
                      <td>{item.consumptionRate} L/m</td>
                      <td>
                        <OperationModeMobileStatus
                          $active={item.status === 'active'}
                        >
                          {item.status || 'inactive'}
                        </OperationModeMobileStatus>
                      </td>
                      <td>
                        {formatCurrentDate(
                          item.updatedAt,
                          EFormatCurrentDate.DEFAULT_SHORT_MONTH,
                        )}
                      </td>
                      <td>
                        <Dropdown
                          trigger={['click']}
                          menu={{
                            items: getMobileActionItems(item),
                            onClick: handleMobileActionClick(item),
                          }}
                        >
                          <OperationModeMobileMoreButton
                            type="button"
                            aria-label={`Open actions for ${item.name}`}
                          >
                            <RiMore2Line />
                          </OperationModeMobileMoreButton>
                        </Dropdown>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </OperationModeMobileTable>
            </OperationModeMobileTableWrap>
          )}
        </>
      ) : (
        <>
          <FilterWrapper>
            <RowFilterWrapper>
              <RowFilter
                filterValues={{
                  search: pageQueries.search,
                  status: pageQueries.status || 'all',
                  filterData: pageQueries.filterData || 'default',
                }}
                filters={[filterData]}
              />
            </RowFilterWrapper>
            {!isRamus2 && (
              <Button type='primary' onClick={handleCreateDevice}>
                Add Constant
              </Button>
            )}
          </FilterWrapper>

          <AppTable
            isLoading={isLoading}
            keyId='operationModeDetailId'
            columns={columns}
            data={sortedData}
            size='small'
            pagination={pagination}
            onChangePage={changePage}
            onChangeLimit={changeLimit}
            // _table={{
            //   scroll:{ x: '100%' }
            // } as any}
          />
        </>
      )}

      {isMobile && (
        <OperationModeMobileFilterDrawer
          title="Filter"
          placement="bottom"
          height="auto"
          open={isMobileFilterOpen}
          onClose={() => setIsMobileFilterOpen(false)}
        >
          <OperationModeMobileFilterForm>
            <OperationModeMobileFilterFields>
              <OperationModeMobileFilterField>
                Search
                <OperationModeMobileSearch
                  allowClear
                  placeholder="Search by name"
                  value={mobileFilterValues.search}
                  onChange={(event) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                />
              </OperationModeMobileFilterField>
              <OperationModeMobileFilterField>
                Status
                <OperationModeMobileFilterSelect
                  value={mobileFilterValues.status}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                  ]}
                  onChange={(value) =>
                    setMobileFilterValues((prev) => ({
                      ...prev,
                      status: String(value),
                    }))
                  }
                />
              </OperationModeMobileFilterField>
              {!isRamus2 && isUserHasPermission([FEATURE + '.DELETE']) && (
                <OperationModeMobileFilterField>
                  Filter Data
                  <OperationModeMobileFilterSelect
                    value={mobileFilterValues.filterData}
                    options={[
                      { value: 'default', label: 'Default' },
                      { value: 'show', label: 'Include Archive Data' },
                    ]}
                    onChange={(value) =>
                      setMobileFilterValues((prev) => ({
                        ...prev,
                        filterData: String(value),
                      }))
                    }
                  />
                </OperationModeMobileFilterField>
              )}
            </OperationModeMobileFilterFields>
            <OperationModeMobileFilterActions>
              <OperationModeMobileFilterApplyButton
                type="primary"
                onClick={handleApplyMobileFilter}
              >
                Apply
              </OperationModeMobileFilterApplyButton>
            </OperationModeMobileFilterActions>
          </OperationModeMobileFilterForm>
        </OperationModeMobileFilterDrawer>
      )}

      <Modal
        title={
          forceDeleteId ? 'Force Delete Confirmation' : 'Delete Confirmation'
        }
        open={isModalDeleteOpen}
        onOk={deleteData}
        okButtonProps={{
          style: {
            background: 'red',
          },
        }}
        onCancel={handleCloseModal}
      >
        {forceDeleteId ? (
          <p>
            Are you sure want to permanently delete this data? This data will be
            deleted and only can't be undo.
          </p>
        ) : (
          <p>
            Are you sure want to delete this data? This data will be archived
            and only User with restore permission can undo this action.
          </p>
        )}
      </Modal>

      <Modal
        title='Restore Confirmation'
        open={isModalRestoreOpen}
        onOk={restoreData}
        okButtonProps={{
          style: {
            background: 'blue',
          },
        }}
        onCancel={handleCloseModal}
      >
        <p>Are you sure want to restore this data?.</p>
      </Modal>

      {!isRamus2 && (
        <ConstantModal
          title={isMobile ? operationModeTitle || 'Operation Mode' : 'Create New Constant'}
          open={isModalCreateOpen}
          onCancel={handleCloseModal}
          footer={''}
          width={isMobile ? 'calc(100vw - 24px)' : undefined}
          centered={isMobile}
          bodyStyle={{
            marginTop: isMobile ? 0 : 20,
          }}
        >
          <OperationModeDetailCreate
            operationModeId={operationModeId}
            onClose={() => {
              fetchList();
              handleCloseModal();
            }}
          />
        </ConstantModal>
      )}

      <ConstantModal
        title={isMobile ? operationModeTitle || 'Operation Mode' : 'Edit Constant'}
        open={isModalEditOpen}
        onCancel={handleCloseModal}
        footer={''}
        width={isMobile ? 'calc(100vw - 24px)' : undefined}
        centered={isMobile}
      >
        {currentData ? (
          <OperationModeDetailEdit
            operationModeId={operationModeId}
            operationModeDetailId={currentData.operationModeDetailId}
            operationModeDetail={currentData}
            ramus2AssetId={ramus2AssetId}
            isRamus2={isRamus2}
            onClose={() => {
              fetchList();
              handleCloseModal();
            }}
          />
        ) : (
          <div>No data available</div>
        )}
      </ConstantModal>
    </React.Fragment>
  );
};

const RowFilterWrapper = styled.div`
  > div {
    > div {
      width: 300px !important;
    }
  }
`;

const FilterWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  justify-content: space-between;
`;

export default OperationModeDetail;
